// Pure view model of the regulatory drift screen: the counts the stat cards show and the filter
// options the snapshot offers. The cards always count the whole ecosystem; only the table follows
// its search and chips.
import { Array as Arr, Order, Schema } from 'effect';
import type { EndpointStateTag, RegulatoryEndpointRow } from '@finnest/power-admin-contracts';

/** The four endpoint states, in the order the status chips offer them. */
export const StateTag = Schema.Literals(['covered', 'drift', 'missing', 'source-blocked']);

/** How many rows are in each state, and in all. */
export type StatusCounts = { readonly total: number } & Readonly<Record<EndpointStateTag, number>>;

/** The counts of `rows`, one per state and the total. */
export const statusCounts = (rows: readonly RegulatoryEndpointRow[]): StatusCounts => {
  const byState = Arr.groupBy(rows, (row) => row.state._tag);
  const count = (tag: EndpointStateTag): number => byState[tag]?.length ?? 0;
  return {
    total: rows.length,
    covered: count('covered'),
    drift: count('drift'),
    missing: count('missing'),
    'source-blocked': count('source-blocked'),
  } satisfies StatusCounts;
};

/** The families and phases the chips offer: each once, in code-unit order. */
export type FilterOptions = {
  readonly families: readonly string[];
  readonly phases: readonly string[];
};

const distinctSorted = (values: readonly string[]): readonly string[] =>
  Arr.sort(Arr.dedupe(values), Order.String);

/** The chip options `rows` offer. */
export const filterOptions = (rows: readonly RegulatoryEndpointRow[]): FilterOptions => ({
  families: distinctSorted(rows.map((row) => row.family)),
  phases: distinctSorted(rows.map((row) => row.phase)),
});
